import { FC } from "react";
import { Button, Input, Label, Popconfirm, Spinner, Stack } from "@altimateai/lego";
import {
  Icon,
  IconBuilding,
  IconLink,
  IconTrash,
  IconUser,
  IconUsersGroup,
  IconWorld,
} from "@tabler/icons-react";
import { CopyIconButton } from "@/components/copyButton";
import { ShareAccessType, ShareLink } from "./types";

const ACCESS_TYPE_ICON: Record<ShareAccessType, Icon> = {
  anyone: IconWorld,
  all_altimate_users: IconBuilding,
  team: IconUsersGroup,
  specific_people: IconUser,
};

const describeAccess = (link: ShareLink): string => {
  switch (link.access_type) {
    case "anyone":
      return "Anyone with the link";
    case "all_altimate_users":
      return "Anyone in this organization";
    case "team": {
      const count = link.allowed_teams?.length ?? 0;
      return `${count} team${count === 1 ? "" : "s"}`;
    }
    case "specific_people": {
      const count = link.allowed_users?.length ?? 0;
      return `${count} ${count === 1 ? "person" : "people"}`;
    }
    // The API client falls back to the unvalidated response when schema
    // validation fails, so an access type outside the union can reach
    // this component at runtime.
    default:
      return "Shared link";
  }
};

interface ShareLinksListProps {
  shareLinks: ShareLink[];
  isLoadingLinks: boolean;
  isDeleting: boolean;
  onRemoveLink: (shareId: string) => void;
  formatExpiration: (expiresAt: string | null) => string;
}

const ShareLinksList: FC<ShareLinksListProps> = ({
  shareLinks,
  isLoadingLinks,
  isDeleting,
  onRemoveLink,
  formatExpiration,
}) => {
  if (isLoadingLinks) {
    return (
      <Stack direction="row" align="center" justify="center" className="al-py-4">
        <Spinner size="md" />
      </Stack>
    );
  }

  if (shareLinks.length === 0) return null;

  return (
    <div className="al-space-y-3">
      <Label>Active Links ({shareLinks.length})</Label>
      <div className="al-space-y-2 al-max-h-[200px] al-overflow-y-auto">
        {shareLinks.map((link: ShareLink) => {
          // Fall back to a generic icon for access types outside the
          // union (reachable when the API client returns an unvalidated
          // response) — rendering `undefined` as a component would crash.
          const AccessIcon = ACCESS_TYPE_ICON[link.access_type] ?? IconLink;
          return (
            <Stack
              direction="column"
              gap="xs"
              key={link.id}
              className="al-p-2 al-rounded-md al-bg-muted"
            >
              <Stack direction="row" align="center" gap="sm">
                <AccessIcon className="al-h-4 al-w-4 al-text-muted-foreground al-flex-shrink-0" />
                <Input value={link.share_url} readOnly className="al-flex-1 al-text-sm" />
                <CopyIconButton value={link.share_url} colorClassName="al-text-foreground" />
                <Popconfirm
                  title="Delete share link"
                  description="Are you sure you want to delete this share link?"
                  onConfirm={() => onRemoveLink(link.id)}
                >
                  <Button variant="outline" size="icon" title="Delete link" disabled={isDeleting}>
                    <IconTrash className="al-h-4 al-w-4 al-text-destructive" />
                  </Button>
                </Popconfirm>
              </Stack>
              <p className="al-text-xs al-text-muted-foreground al-pl-6">
                {describeAccess(link)} • {formatExpiration(link.expires_at)}
              </p>
            </Stack>
          );
        })}
      </div>
    </div>
  );
};

export default ShareLinksList;
